import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { GetHumanTasksResponse, AgentEvents, HumanTask } from '@dreamai/types';
import { usePusher } from '../../../contexts/PusherContext';
import api from '../../../api';

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const FIVE_MINUTES_MS = 5 * 60 * 1000;

/**
 * Check if a task has a deadline within the next 2 hours
 */
const isUrgentTask = (task: HumanTask): boolean => {
  const deadline = (task as any).deadline;
  if (!deadline) return false;
  const deadlineTime = new Date(deadline).getTime();
  const now = Date.now();
  return deadlineTime > now && deadlineTime - now <= TWO_HOURS_MS;
};

/**
 * Request permission for desktop notifications
 */
const requestNotificationPermission = async (): Promise<boolean> => {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

/**
 * Show desktop notification for urgent tasks
 */
const showUrgentTaskNotification = (urgentCount: number) => {
  if (!('Notification' in window) || Notification.permission !== 'granted')
    return;

  new Notification('Urgent Tasks', {
    body: `You have ${urgentCount} task${urgentCount > 1 ? 's' : ''} due within 2 hours`,
    icon: '/favicon.ico',
    tag: 'urgent-tasks', // Prevents duplicate notifications
  });
};

/**
 * Hook to fetch pending human tasks count
 * Used by the navigation to display the badge count for ops users
 * @param enabled - Whether to enable the hook (should only be true for ops users)
 */
export const useTasksCount = (enabled: boolean = false) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);
  const opsChannel = pusherContext?.opsChannel;
  const [pendingCount, setPendingCount] = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const previousUrgentCountRef = useRef(0);
  const hasNotificationPermission = useRef(false);

  // Request notification permission on mount (only if enabled)
  useEffect(() => {
    if (!enabled) return;
    requestNotificationPermission().then((granted) => {
      hasNotificationPermission.current = granted;
    });
  }, [enabled]);

  const fetchTasks = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        params: { status: 'pending' },
      });

      if (response.data.success && response.data.tasks) {
        const tasks = response.data.tasks;
        setPendingCount(tasks.length);

        // Count urgent tasks (deadline within 2 hours)
        const urgent = tasks.filter(isUrgentTask).length;
        setUrgentCount(urgent);

        // Show notification if urgent count increased
        if (
          urgent > 0 &&
          urgent > previousUrgentCountRef.current &&
          hasNotificationPermission.current
        ) {
          showUrgentTaskNotification(urgent);
        }
        previousUrgentCountRef.current = urgent;
      }
    } catch (error) {
      console.error('Error fetching human tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [enabled, getAccessTokenSilently]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Poll every 5 minutes for urgent task updates (only if enabled)
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(() => {
      fetchTasks();
    }, FIVE_MINUTES_MS);

    return () => clearInterval(interval);
  }, [enabled, fetchTasks]);

  // Listen for task created and accepted events on ops channel (only if enabled)
  useEffect(() => {
    if (!enabled || !opsChannel) return;

    const handleTaskChange = () => {
      fetchTasks();
    };

    opsChannel.bind(AgentEvents.HUMAN_TASK_CREATED, handleTaskChange);
    opsChannel.bind(AgentEvents.HUMAN_TASK_ACCEPTED, handleTaskChange);
    opsChannel.bind(AgentEvents.HUMAN_TASK_CANCELLED, handleTaskChange);

    return () => {
      opsChannel.unbind(AgentEvents.HUMAN_TASK_CREATED, handleTaskChange);
      opsChannel.unbind(AgentEvents.HUMAN_TASK_ACCEPTED, handleTaskChange);
      opsChannel.unbind(AgentEvents.HUMAN_TASK_CANCELLED, handleTaskChange);
    };
  }, [enabled, opsChannel, fetchTasks]);

  return {
    pendingCount,
    urgentCount,
    loading,
    refresh: fetchTasks,
  };
};
